// The device command vocabulary, in one place.
//
// Sourced from the working MQTT console (mqttbox-reactjs MainPage.js) and the
// Flutter apps, so every screen sends the same literal strings.
//
// ── CASE RULE, straight from the firmware ──────────────────────────────────
//
//   if (!tempword.startsWith("REMOVEWIFI") && !tempword.startsWith("ADDWIFI")) {
//       tempword.toUpperCase();
//   }
//
// Everything EXCEPT the wifi commands is upper-cased before matching, so case is
// irrelevant for them and the house style is ALL LOWERCASE.
//
// ⚠️ ADDWIFI / REMOVEWIFI ARE DELIBERATELY SKIPPED by that upper-casing, because
// the payload carries an SSID and a password and those ARE case-sensitive —
// upper-casing them would corrupt the credentials. So these take no change of
// any kind, not case and not spacing. Exact literals:
//
//     ADDWIFIssid=<ssid>pass=<password>        (MQTT, no separators at all)
//     REMOVEWIFIssid=<ssid>pass=<password>     (MQTT, no separators at all)
//     ADDWIFI ssid=<ssid>&pass=<password>      (BLE — space and '&', different!)
//
// The MQTT and BLE provisioning payloads are NOT interchangeable: MQTT runs the
// fields together, BLE separates them with a space and an '&'. Reusing one for
// the other silently fails to parse.

// ── Simple word commands (lowercase) ───────────────────────────────────────
export const CMD_ALL_ON = 'allon';
export const CMD_ALL_OFF = 'alloff';
export const CMD_S05 = 's05';
// The firmware accepts UPDATECODE / UPDATE CODE / CODEUPDATE / CODE UPDATE, so
// the space is optional. This spelling matches the MQTT console's presets.
export const CMD_UPDATE_CODE = 'updatecode';
export const CMD_GET_ALL_WIFI = 'getallwifi';
export const CMD_DEBUG_ON = 'debugon';
export const CMD_DEBUG_OFF = 'debugoff';

// Shown as shortcut buttons in the message console, in this order.
export const PRESET_COMMANDS = [
  CMD_ALL_ON, CMD_ALL_OFF, CMD_S05, CMD_UPDATE_CODE, CMD_GET_ALL_WIFI,
];
export const DEBUG_COMMANDS = [CMD_DEBUG_ON, CMD_DEBUG_OFF];

// ── Wi-Fi commands — EXACT LITERALS, do not normalise ──────────────────────
export const buildAddWifiMqtt = (ssid, password) =>
  `ADDWIFIssid=${ssid}pass=${password}`;

export const buildRemoveWifiMqtt = (ssid, password) =>
  `REMOVEWIFIssid=${ssid}pass=${password}`;

// The device answers `getallwifi` with entries separated by ';', each a
// "ssid,pass" pair. -> [{ ssid, pass }]
export function parseWifiList(payload) {
  return String(payload || '')
    .split(';')
    .map((entry) => {
      const parts = entry.split(',');
      if (parts.length !== 2) return null;
      const ssid = parts[0].trim();
      return ssid ? { ssid, pass: parts[1].trim() } : null;
    })
    .filter(Boolean);
}

// ── Topic ──────────────────────────────────────────────────────────────────
// A bare unique number means the device's command topic.
export const toDeviceTopic = (topicOrUno) => {
  const t = String(topicOrUno || '').trim();
  return t.includes('/') ? t : `${t}/device`;
};
